import React, { useState, useEffect, useMemo } from 'react';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  subMonths,
  addMonths,
  startOfYear,
  endOfYear,
  subYears,
  addYears,
  subDays,
  addDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  isSameMonth,
  isSameYear,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  X,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  FileText,
  BarChart3,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  DoughnutController,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  DoughnutController,
  Filler,
  Title,
  Tooltip,
  Legend
);

type TimeRange = 'week' | 'month' | 'year' | 'all';
type SortOption = 'name' | 'crates' | 'pallets' | 'entries' | 'invoice';

interface AggregatedData {
  crates: number;
  pallets: number;
  entries: number;
  invoiced: number;
  notInvoiced: number;
}

interface FarmerStats {
  id: string;
  name: string;
  current: AggregatedData;
  previous: AggregatedData;
  dayData: Map<string, AggregatedData>;
  weekData: Map<string, AggregatedData>;
  monthData: Map<string, AggregatedData>;
  yearData: Map<string, AggregatedData>;
  invoicedPercentage: number;
}

interface GlobalStats {
  current: AggregatedData;
  previous: AggregatedData;
  topFarmers: Array<{ id: string; name: string; crates: number; pallets: number }>;
  topCustomers: Array<{ name: string; crates: number; pallets: number }>;
  productBreakdown: Array<{ description: string; crates: number; pallets: number }>;
  needsAttention: Array<{ id: string; name: string; invoicedPercentage: number; notInvoicedCount: number }>;
}

const COLORS = {
  primary: '#4a3f35',
  accent: '#8cc63f',
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  gray: '#9ca3af',
};

const CHART_COLORS = [
  '#4a3f35',
  '#8cc63f',
  '#22c55e',
  '#eab308',
  '#ef4444',
  '#3b82f6',
  '#a855f7',
  '#f97316',
  '#14b8a6',
  '#6366f1',
];

function getDateLabel(timeRange: TimeRange, currentDate: Date): string {
  switch (timeRange) {
    case 'week': {
      const start = subDays(currentDate, 6);
      return `${format(start, 'MMM d')} – ${format(currentDate, 'MMM d, yyyy')}`;
    }
    case 'month':
      return format(currentDate, 'MMMM yyyy');
    case 'year':
      return format(currentDate, 'yyyy');
    case 'all':
      return 'All Time';
  }
}

function isCurrentPeriod(entryDate: Date, timeRange: TimeRange, currentDate: Date): boolean {
  switch (timeRange) {
    case 'week': {
      const start = subDays(currentDate, 6);
      return entryDate >= start && entryDate <= currentDate;
    }
    case 'month':
      return isSameMonth(entryDate, currentDate);
    case 'year':
      return isSameYear(entryDate, currentDate);
    case 'all':
      return true;
  }
}

function isPreviousPeriod(entryDate: Date, timeRange: TimeRange, currentDate: Date): boolean {
  switch (timeRange) {
    case 'week': {
      const start = subDays(currentDate, 13);
      const end = subDays(currentDate, 7);
      return entryDate >= start && entryDate <= end;
    }
    case 'month': {
      const prev = subMonths(currentDate, 1);
      return isSameMonth(entryDate, prev);
    }
    case 'year': {
      const prev = subYears(currentDate, 1);
      return isSameYear(entryDate, prev);
    }
    case 'all':
      return false;
  }
}

function getWeekOfMonthKey(date: Date): string {
  const day = date.getDate();
  const weekNum = Math.ceil(day / 7);
  return `Week ${weekNum}`;
}

function getQueryRange(timeRange: TimeRange, currentDate: Date): { start: Date; end: Date } | null {
  switch (timeRange) {
    case 'week': {
      const end = currentDate;
      const start = subDays(end, 13);
      return { start, end };
    }
    case 'month': {
      const currentStart = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const start = subMonths(currentStart, 1);
      return { start, end };
    }
    case 'year': {
      const currentStart = startOfYear(currentDate);
      const end = endOfYear(currentDate);
      const start = subYears(currentStart, 1);
      return { start, end };
    }
    case 'all':
      return null;
  }
}

function csvEscape(val: string | number): string {
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function sortFarmers(farmers: FarmerStats[], sortBy: SortOption): FarmerStats[] {
  const sorted = [...farmers];
  switch (sortBy) {
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'crates':
      return sorted.sort((a, b) => b.current.crates - a.current.crates);
    case 'pallets':
      return sorted.sort((a, b) => b.current.pallets - a.current.pallets);
    case 'entries':
      return sorted.sort((a, b) => b.current.entries - a.current.entries);
    case 'invoice': {
      const pctA = a.current.entries > 0 ? a.current.invoiced / a.current.entries : 0;
      const pctB = b.current.entries > 0 ? b.current.invoiced / b.current.entries : 0;
      return sorted.sort((a, b) => pctA - pctB);
    }
  }
}

function getFarmerChartData(
  farmer: FarmerStats,
  timeRange: TimeRange,
  currentDate: Date,
  allFarmers: FarmerStats[]
): {
  labels: string[];
  cratesData: number[];
  palletsData: number[];
  invoicedData: number[];
  notInvoicedData: number[];
} {
  switch (timeRange) {
    case 'week': {
      const days = eachDayOfInterval({ start: subDays(currentDate, 6), end: currentDate });
      return {
        labels: days.map(d => format(d, 'EEE d')),
        cratesData: days.map(d => farmer.dayData.get(format(d, 'yyyy-MM-dd'))?.crates || 0),
        palletsData: days.map(d => farmer.dayData.get(format(d, 'yyyy-MM-dd'))?.pallets || 0),
        invoicedData: days.map(d => farmer.dayData.get(format(d, 'yyyy-MM-dd'))?.invoiced || 0),
        notInvoicedData: days.map(d => farmer.dayData.get(format(d, 'yyyy-MM-dd'))?.notInvoiced || 0),
      };
    }
    case 'month': {
      const weeksInMonth = Math.ceil(endOfMonth(currentDate).getDate() / 7);
      const labels = Array.from({ length: weeksInMonth }, (_, i) => `Week ${i + 1}`);
      return {
        labels,
        cratesData: labels.map(label => farmer.weekData.get(label)?.crates || 0),
        palletsData: labels.map(label => farmer.weekData.get(label)?.pallets || 0),
        invoicedData: labels.map(label => farmer.weekData.get(label)?.invoiced || 0),
        notInvoicedData: labels.map(label => farmer.weekData.get(label)?.notInvoiced || 0),
      };
    }
    case 'year': {
      const months = eachMonthOfInterval({ start: startOfYear(currentDate), end: endOfYear(currentDate) });
      return {
        labels: months.map(d => format(d, 'MMM')),
        cratesData: months.map(d => farmer.monthData.get(format(d, 'yyyy-MM'))?.crates || 0),
        palletsData: months.map(d => farmer.monthData.get(format(d, 'yyyy-MM'))?.pallets || 0),
        invoicedData: months.map(d => farmer.monthData.get(format(d, 'yyyy-MM'))?.invoiced || 0),
        notInvoicedData: months.map(d => farmer.monthData.get(format(d, 'yyyy-MM'))?.notInvoiced || 0),
      };
    }
    case 'all': {
      const allYears = new Set<string>();
      allFarmers.forEach(f => f.yearData.forEach((_, year) => allYears.add(year)));
      const sortedYears = Array.from(allYears).sort();
      return {
        labels: sortedYears,
        cratesData: sortedYears.map(year => farmer.yearData.get(year)?.crates || 0),
        palletsData: sortedYears.map(year => farmer.yearData.get(year)?.pallets || 0),
        invoicedData: sortedYears.map(year => farmer.yearData.get(year)?.invoiced || 0),
        notInvoicedData: sortedYears.map(year => farmer.yearData.get(year)?.notInvoiced || 0),
      };
    }
  }
}

function TrendBadge({ current, previous, inverted = false, suffix = '' }: {
  current: number;
  previous: number;
  inverted?: boolean;
  suffix?: string;
}) {
  if (previous === 0) {
    if (current === 0) return <span className="text-xs text-gray-400">—</span>;
    return <span className="text-xs text-green-600 font-medium">NEW</span>;
  }

  const change = ((current - previous) / previous) * 100;
  const isPositive = change > 0;
  const isGood = inverted ? !isPositive : isPositive;
  const Icon = isPositive ? ArrowUpRight : ArrowDownRight;

  return (
    <span className={clsx('text-xs font-medium flex items-center gap-0.5', isGood ? 'text-green-600' : 'text-red-600')}>
      <Icon className="h-3 w-3" />
      {Math.abs(change).toFixed(1)}%{suffix}
    </span>
  );
}

function InvoiceProgressBar({ percentage, size = 'md' }: { percentage: number; size?: 'sm' | 'md' }) {
  let color = 'bg-red-500';
  if (percentage >= 80) color = 'bg-green-500';
  else if (percentage >= 50) color = 'bg-yellow-500';

  const height = size === 'sm' ? 'h-1.5' : 'h-2.5';

  return (
    <div className="flex items-center gap-2">
      <div className={clsx('flex-1 bg-gray-200 rounded-full', height)}>
        <div className={clsx('rounded-full transition-all', color, height)} style={{ width: `${Math.min(100, percentage)}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 w-10 text-right">{percentage.toFixed(0)}%</span>
    </div>
  );
}

function SkeletonCard({ height = 'h-24' }: { height?: string }) {
  return <div className={clsx('bg-gray-100 rounded-lg animate-pulse', height)} />;
}

function SkeletonChart() {
  return <div className="bg-gray-100 rounded-lg animate-pulse h-72" />;
}

function EmptyChart({ message = 'No data for this period' }: { message?: string }) {
  return (
    <div className="h-64 flex flex-col items-center justify-center text-gray-400">
      <BarChart3 className="h-10 w-10 mb-2" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

const commonChartOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 12 },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(74, 63, 53, 0.9)',
      padding: 12,
      cornerRadius: 8,
      titleFont: { size: 13, weight: 'bold' },
      bodyFont: { size: 12 },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
        drawBorder: false,
      },
      ticks: { font: { size: 11 } },
    },
    x: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
  },
};

const lineChartOptions: any = {
  ...commonChartOptions,
  elements: {
    line: { tension: 0.4 },
    point: { radius: 3, hoverRadius: 5 },
  },
};

const horizontalBarOptions: any = {
  ...commonChartOptions,
  indexAxis: 'y',
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
        drawBorder: false,
      },
      ticks: { font: { size: 11 } },
    },
    y: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
  },
};

const doughnutOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 12 },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(74, 63, 53, 0.9)',
      padding: 12,
      cornerRadius: 8,
    },
  },
  cutout: '60%',
};

export default function Stats() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [farmers, setFarmers] = useState<FarmerStats[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [selectedFarmer, setSelectedFarmer] = useState<FarmerStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFarmers();

    const subscription = supabase
      .channel('stats_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'entries',
        },
        () => fetchFarmers()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [currentDate, timeRange]);

  async function fetchFarmers() {
    try {
      setLoading(true);

      const range = getQueryRange(timeRange, currentDate);

      let query = supabase
        .from('entries')
        .select(`
          id,
          date,
          invoiced,
          farmer_id,
          farmer:farmers(id, name),
          customer:customers(id, name),
          products(
            id,
            description,
            crates,
            pallets
          )
        `)
        .order('date', { ascending: true });

      if (range) {
        query = query.gte('date', format(range.start, 'yyyy-MM-dd'))
          .lte('date', format(range.end, 'yyyy-MM-dd'));
      } else {
        query = query.limit(10000);
      }

      const { data: entriesData, error: entriesError } = await query;

      if (entriesError) throw entriesError;

      const farmerStats = new Map<string, FarmerStats>();
      const customerStats = new Map<string, { name: string; crates: number; pallets: number }>();
      const productStats = new Map<string, { description: string; crates: number; pallets: number }>();
      const globalCurrent: AggregatedData = { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 };
      const globalPrevious: AggregatedData = { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 };

      const updateAgg = (agg: AggregatedData, crates: number, pallets: number, invoiced: boolean) => {
        agg.crates += crates;
        agg.pallets += pallets;
        agg.entries += 1;
        if (invoiced) agg.invoiced += 1;
        else agg.notInvoiced += 1;
      };

      (entriesData || []).forEach(entry => {
        if (!entry.farmer) return;

        const entryDate = parseISO(entry.date);
        const isCurrent = isCurrentPeriod(entryDate, timeRange, currentDate);
        const isPrevious = isPreviousPeriod(entryDate, timeRange, currentDate);

        const crates = entry.products.reduce((sum: number, p: any) => sum + (Number(p.crates) || 0), 0);
        const pallets = entry.products.reduce((sum: number, p: any) => sum + (Number(p.pallets) || 0), 0);

        if (isCurrent) {
          updateAgg(globalCurrent, crates, pallets, entry.invoiced);
        } else if (isPrevious) {
          updateAgg(globalPrevious, crates, pallets, entry.invoiced);
        }

        const farmerId = entry.farmer.id;
        let farmer = farmerStats.get(farmerId);
        if (!farmer) {
          farmer = {
            id: farmerId,
            name: entry.farmer.name,
            current: { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 },
            previous: { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 },
            dayData: new Map(),
            weekData: new Map(),
            monthData: new Map(),
            yearData: new Map(),
            invoicedPercentage: 0,
          };
          farmerStats.set(farmerId, farmer);
        }

        if (isCurrent) {
          updateAgg(farmer.current, crates, pallets, entry.invoiced);

          const dayKey = format(entryDate, 'yyyy-MM-dd');
          const dayAgg = farmer.dayData.get(dayKey) || { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 };
          updateAgg(dayAgg, crates, pallets, entry.invoiced);
          farmer.dayData.set(dayKey, dayAgg);

          const weekKey = getWeekOfMonthKey(entryDate);
          const weekAgg = farmer.weekData.get(weekKey) || { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 };
          updateAgg(weekAgg, crates, pallets, entry.invoiced);
          farmer.weekData.set(weekKey, weekAgg);

          const monthKey = format(entryDate, 'yyyy-MM');
          const monthAgg = farmer.monthData.get(monthKey) || { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 };
          updateAgg(monthAgg, crates, pallets, entry.invoiced);
          farmer.monthData.set(monthKey, monthAgg);

          const yearKey = format(entryDate, 'yyyy');
          const yearAgg = farmer.yearData.get(yearKey) || { crates: 0, pallets: 0, entries: 0, invoiced: 0, notInvoiced: 0 };
          updateAgg(yearAgg, crates, pallets, entry.invoiced);
          farmer.yearData.set(yearKey, yearAgg);

          if (entry.customer) {
            const cust = customerStats.get(entry.customer.id) || { name: entry.customer.name, crates: 0, pallets: 0 };
            cust.crates += crates;
            cust.pallets += pallets;
            customerStats.set(entry.customer.id, cust);
          }

          entry.products.forEach((product: any) => {
            const prod = productStats.get(product.description) || { description: product.description, crates: 0, pallets: 0 };
            prod.crates += Number(product.crates) || 0;
            prod.pallets += Number(product.pallets) || 0;
            productStats.set(product.description, prod);
          });
        } else if (isPrevious) {
          updateAgg(farmer.previous, crates, pallets, entry.invoiced);
        }
      });

      const processedFarmers = Array.from(farmerStats.values()).map(farmer => ({
        ...farmer,
        invoicedPercentage: farmer.current.entries > 0
          ? (farmer.current.invoiced / farmer.current.entries) * 100
          : 0,
      }));

      const globalStatsData: GlobalStats = {
        current: globalCurrent,
        previous: globalPrevious,
        topFarmers: Array.from(farmerStats.values())
          .map(f => ({ id: f.id, name: f.name, crates: f.current.crates, pallets: f.current.pallets }))
          .sort((a, b) => b.crates - a.crates)
          .slice(0, 10),
        topCustomers: Array.from(customerStats.values())
          .sort((a, b) => b.crates - a.crates)
          .slice(0, 10),
        productBreakdown: Array.from(productStats.values())
          .sort((a, b) => b.crates - a.crates)
          .slice(0, 10),
        needsAttention: Array.from(farmerStats.values())
          .map(f => ({
            id: f.id,
            name: f.name,
            invoicedPercentage: f.current.entries > 0 ? (f.current.invoiced / f.current.entries) * 100 : 0,
            notInvoicedCount: f.current.notInvoiced,
          }))
          .filter(f => f.notInvoicedCount > 0)
          .sort((a, b) => a.invoicedPercentage - b.invoicedPercentage)
          .slice(0, 10),
      };

      setFarmers(processedFarmers);
      setGlobalStats(globalStatsData);

      if (selectedFarmer) {
        const updatedFarmer = processedFarmers.find(f => f.id === selectedFarmer.id);
        if (updatedFarmer) {
          setSelectedFarmer(updatedFarmer);
        }
      }
    } catch (error) {
      console.error('Error fetching farmers:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }

  function handlePrev() {
    switch (timeRange) {
      case 'week':
        setCurrentDate(subDays(currentDate, 7));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(subYears(currentDate, 1));
        break;
      case 'all':
        break;
    }
  }

  function handleNext() {
    switch (timeRange) {
      case 'week':
        setCurrentDate(addDays(currentDate, 7));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(addYears(currentDate, 1));
        break;
      case 'all':
        break;
    }
  }

  function handleExportGlobalCSV() {
    if (!globalStats) return;
    try {
      const period = getDateLabel(timeRange, currentDate);
      const rows = [
        ['FarmArk - Global Statistics Report'],
        ['Period', period],
        ['Generated', format(new Date(), 'MMMM d, yyyy')],
        [],
        ['Summary'],
        ['Metric', 'Value'],
        ['Total Entries', globalStats.current.entries],
        ['Total Crates', globalStats.current.crates],
        ['Total Pallets', globalStats.current.pallets],
        ['Invoiced Entries', globalStats.current.invoiced],
        ['Not Invoiced Entries', globalStats.current.notInvoiced],
        ['Invoiced Percentage', `${(globalStats.current.entries > 0 ? (globalStats.current.invoiced / globalStats.current.entries) * 100 : 0).toFixed(1)}%`],
        [],
        ['Top Farmers'],
        ['Rank', 'Farmer', 'Crates', 'Pallets'],
        ...globalStats.topFarmers.map((f, i) => [i + 1, f.name, f.crates, f.pallets]),
        [],
        ['Top Customers'],
        ['Rank', 'Customer', 'Crates', 'Pallets'],
        ...globalStats.topCustomers.map((c, i) => [i + 1, c.name, c.crates, c.pallets]),
        [],
        ['Product Breakdown'],
        ['Product', 'Crates', 'Pallets'],
        ...globalStats.productBreakdown.map(p => [p.description, p.crates, p.pallets]),
      ];

      const csv = rows.map(row => row.map(csvEscape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `global-stats-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Global statistics exported to CSV');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  }

  function handleExportFarmerCSV(farmer: FarmerStats) {
    try {
      const period = getDateLabel(timeRange, currentDate);
      const { labels, cratesData, palletsData, invoicedData, notInvoicedData } = getFarmerChartData(farmer, timeRange, currentDate, farmers);

      const rows = [
        [`${farmer.name} - Statistics Report`],
        ['Period', period],
        ['Generated', format(new Date(), 'MMMM d, yyyy')],
        [],
        ['Overview'],
        ['Metric', 'Value'],
        ['Total Entries', farmer.current.entries],
        ['Total Crates', farmer.current.crates],
        ['Total Pallets', farmer.current.pallets],
        ['Invoiced Entries', farmer.current.invoiced],
        ['Not Invoiced Entries', farmer.current.notInvoiced],
        ['Invoiced Percentage', `${farmer.invoicedPercentage.toFixed(1)}%`],
        ['Avg Crates / Entry', farmer.current.entries > 0 ? (farmer.current.crates / farmer.current.entries).toFixed(1) : '0'],
        ['Avg Pallets / Entry', farmer.current.entries > 0 ? (farmer.current.pallets / farmer.current.entries).toFixed(1) : '0'],
        [],
        ['Trends (vs previous period)'],
        ['Metric', 'Current', 'Previous', 'Change %'],
        ['Entries', farmer.current.entries, farmer.previous.entries, farmer.previous.entries > 0 ? (((farmer.current.entries - farmer.previous.entries) / farmer.previous.entries) * 100).toFixed(1) : 'N/A'],
        ['Crates', farmer.current.crates, farmer.previous.crates, farmer.previous.crates > 0 ? (((farmer.current.crates - farmer.previous.crates) / farmer.previous.crates) * 100).toFixed(1) : 'N/A'],
        ['Pallets', farmer.current.pallets, farmer.previous.pallets, farmer.previous.pallets > 0 ? (((farmer.current.pallets - farmer.previous.pallets) / farmer.previous.pallets) * 100).toFixed(1) : 'N/A'],
        [],
        ['Period Breakdown'],
        ['Period', 'Crates', 'Pallets', 'Invoiced', 'Not Invoiced'],
        ...labels.map((label, i) => [label, cratesData[i], palletsData[i], invoicedData[i], notInvoicedData[i]]),
      ];

      const csv = rows.map(row => row.map(csvEscape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${farmer.name.toLowerCase().replace(/\s+/g, '-')}-stats-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Statistics exported to CSV');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  }

  function handleExportFarmerPDF(farmer: FarmerStats) {
    try {
      const period = getDateLabel(timeRange, currentDate);
      const { labels, cratesData, palletsData, invoicedData, notInvoicedData } = getFarmerChartData(farmer, timeRange, currentDate, farmers);

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`${farmer.name} - Statistics Report`, 14, 20);

      doc.setFontSize(11);
      doc.text(`Period: ${period}`, 14, 30);
      doc.text(`Generated: ${format(new Date(), 'MMMM d, yyyy')}`, 14, 37);

      autoTable(doc, {
        startY: 45,
        head: [['Metric', 'Value']],
        body: [
          ['Total Entries', farmer.current.entries],
          ['Total Crates', farmer.current.crates],
          ['Total Pallets', farmer.current.pallets],
          ['Invoiced Entries', farmer.current.invoiced],
          ['Not Invoiced Entries', farmer.current.notInvoiced],
          ['Invoiced Percentage', `${farmer.invoicedPercentage.toFixed(1)}%`],
          ['Avg Crates / Entry', farmer.current.entries > 0 ? (farmer.current.crates / farmer.current.entries).toFixed(1) : '0'],
          ['Avg Pallets / Entry', farmer.current.entries > 0 ? (farmer.current.pallets / farmer.current.entries).toFixed(1) : '0'],
        ],
        headStyles: { fillColor: [74, 63, 53] },
      });

      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 10,
        head: [['Period', 'Crates', 'Pallets', 'Invoiced', 'Not Invoiced']],
        body: labels.map((label, i) => [
          label,
          cratesData[i],
          palletsData[i],
          invoicedData[i],
          notInvoicedData[i],
        ]),
        headStyles: { fillColor: [140, 198, 63] },
      });

      doc.save(`${farmer.name.toLowerCase().replace(/\s+/g, '-')}-stats-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('Statistics exported to PDF');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  }

  const filteredFarmers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return farmers;
    return farmers.filter(f => f.name.toLowerCase().includes(term));
  }, [farmers, searchTerm]);

  const sortedFarmers = useMemo(() => {
    return sortFarmers(filteredFarmers, sortBy);
  }, [filteredFarmers, sortBy]);

  const selectedFarmerData = useMemo(() => {
    if (!selectedFarmer) return null;
    return getFarmerChartData(selectedFarmer, timeRange, currentDate, farmers);
  }, [selectedFarmer, timeRange, currentDate, farmers]);

  const globalInvoicedPct = useMemo(() => {
    if (!globalStats) return 0;
    return globalStats.current.entries > 0
      ? (globalStats.current.invoiced / globalStats.current.entries) * 100
      : 0;
  }, [globalStats]);

  const globalInvoiceDoughnutData = useMemo(() => {
    if (!globalStats) return null;
    return {
      labels: ['Invoiced', 'Not Invoiced'],
      datasets: [{
        data: [globalStats.current.invoiced, globalStats.current.notInvoiced],
        backgroundColor: [COLORS.green, COLORS.red],
        borderWidth: 0,
      }],
    };
  }, [globalStats]);

  const topFarmersChartData = useMemo(() => {
    if (!globalStats) return null;
    return {
      labels: globalStats.topFarmers.map(f => f.name),
      datasets: [{
        label: 'Crates',
        data: globalStats.topFarmers.map(f => f.crates),
        backgroundColor: COLORS.primary,
        borderRadius: 4,
      }],
    };
  }, [globalStats]);

  const topCustomersChartData = useMemo(() => {
    if (!globalStats) return null;
    return {
      labels: globalStats.topCustomers.map(c => c.name),
      datasets: [{
        label: 'Crates',
        data: globalStats.topCustomers.map(c => c.crates),
        backgroundColor: COLORS.accent,
        borderRadius: 4,
      }],
    };
  }, [globalStats]);

  const productBreakdownData = useMemo(() => {
    if (!globalStats) return null;
    return {
      labels: globalStats.productBreakdown.map(p => p.description),
      datasets: [{
        data: globalStats.productBreakdown.map(p => p.crates),
        backgroundColor: CHART_COLORS,
        borderWidth: 0,
      }],
    };
  }, [globalStats]);

  const farmerLineData = useMemo(() => {
    if (!selectedFarmerData) return null;
    return {
      labels: selectedFarmerData.labels,
      datasets: [
        {
          label: 'Crates',
          data: selectedFarmerData.cratesData,
          borderColor: COLORS.primary,
          backgroundColor: `${COLORS.primary}33`,
          fill: true,
        },
        {
          label: 'Pallets',
          data: selectedFarmerData.palletsData,
          borderColor: COLORS.accent,
          backgroundColor: `${COLORS.accent}33`,
          fill: true,
        },
      ],
    };
  }, [selectedFarmerData]);

  const farmerInvoiceBarData = useMemo(() => {
    if (!selectedFarmerData) return null;
    return {
      labels: selectedFarmerData.labels,
      datasets: [
        {
          label: 'Invoiced',
          data: selectedFarmerData.invoicedData,
          backgroundColor: COLORS.green,
          borderRadius: 4,
        },
        {
          label: 'Not Invoiced',
          data: selectedFarmerData.notInvoicedData,
          backgroundColor: COLORS.red,
          borderRadius: 4,
        },
      ],
    };
  }, [selectedFarmerData]);

  const farmerInvoiceDoughnutData = useMemo(() => {
    if (!selectedFarmer) return null;
    return {
      labels: ['Invoiced', 'Not Invoiced'],
      datasets: [{
        data: [selectedFarmer.current.invoiced, selectedFarmer.current.notInvoiced],
        backgroundColor: [COLORS.green, COLORS.red],
        borderWidth: 0,
      }],
    };
  }, [selectedFarmer]);

  const dateLabel = getDateLabel(timeRange, currentDate);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <h1 className="text-2xl font-bold text-[#4a3f35]">Statistics</h1>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              className="rounded-md border-gray-300 shadow-sm focus:border-[#8cc63f] focus:ring-[#8cc63f] sm:text-sm"
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="all">All Time</option>
            </select>
            {timeRange !== 'all' && (
              <div className="flex items-center gap-1 bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={handlePrev}
                  className="p-2 rounded-l-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>
                <span className="text-sm font-medium text-gray-900 min-w-[140px] text-center px-2">
                  {dateLabel}
                </span>
                <button
                  onClick={handleNext}
                  className="p-2 rounded-r-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            )}
            {timeRange === 'all' && (
              <span className="text-sm font-medium text-gray-900 px-3 py-2 bg-white rounded-lg shadow-sm border border-gray-200">
                {dateLabel}
              </span>
            )}
            {!selectedFarmer && (
              <button
                onClick={handleExportGlobalCSV}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Farmer List Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow sticky top-4">
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search farmers..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#8cc63f] focus:border-[#8cc63f] text-sm"
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
                <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">
                    {filteredFarmers.length} result{filteredFarmers.length !== 1 ? 's' : ''}
                  </span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="text-xs border-gray-300 rounded-md focus:ring-[#8cc63f] focus:border-[#8cc63f]"
                  >
                    <option value="name">Name</option>
                    <option value="crates">Crates ↓</option>
                    <option value="pallets">Pallets ↓</option>
                    <option value="entries">Entries ↓</option>
                    <option value="invoice">Invoice % ↑</option>
                  </select>
                </div>
              </div>
              <div className="divide-y divide-gray-200 max-h-[40vh] sm:max-h-[calc(100vh-300px)] overflow-y-auto">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="space-y-2">
                        <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse" />
                        <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse" />
                        <div className="h-2 bg-gray-100 rounded w-full animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : sortedFarmers.length === 0 ? (
                  <div className="p-6 text-center">
                    <Filter className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No farmers found</p>
                  </div>
                ) : (
                  sortedFarmers.map(farmer => (
                    <div
                      key={farmer.id}
                      className={clsx(
                        'p-4 cursor-pointer transition-colors',
                        selectedFarmer?.id === farmer.id
                          ? 'bg-[#8cc63f]/10 border-l-4 border-[#8cc63f]'
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      )}
                      onClick={() => setSelectedFarmer(farmer)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900 text-sm truncate pr-2">{farmer.name}</div>
                        {timeRange !== 'all' && (
                          <TrendBadge
                            current={farmer.current.entries}
                            previous={farmer.previous.entries}
                          />
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                        <span>{farmer.current.crates} crates</span>
                        <span>•</span>
                        <span>{farmer.current.pallets} pallets</span>
                        <span>•</span>
                        <span>{farmer.current.entries} entries</span>
                      </div>
                      <div className="mt-2">
                        <InvoiceProgressBar percentage={farmer.invoicedPercentage} size="sm" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {selectedFarmer ? (
              /* Farmer Detail View */
              <>
                {/* Detail Header */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold text-[#4a3f35]">
                        {selectedFarmer.name}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        {dateLabel}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleExportFarmerCSV(selectedFarmer)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        CSV
                      </button>
                      <button
                        onClick={() => handleExportFarmerPDF(selectedFarmer)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        PDF
                      </button>
                      <button
                        onClick={() => setSelectedFarmer(null)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-[#4a3f35]" />
                      <p className="text-xs font-medium text-gray-600">Entries</p>
                    </div>
                    <p className="text-xl font-bold text-[#4a3f35]">{selectedFarmer.current.entries}</p>
                    {timeRange !== 'all' && (
                      <TrendBadge current={selectedFarmer.current.entries} previous={selectedFarmer.previous.entries} />
                    )}
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-[#8cc63f]" />
                      <p className="text-xs font-medium text-gray-600">Crates</p>
                    </div>
                    <p className="text-xl font-bold text-[#4a3f35]">{selectedFarmer.current.crates}</p>
                    {timeRange !== 'all' && (
                      <TrendBadge current={selectedFarmer.current.crates} previous={selectedFarmer.previous.crates} />
                    )}
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-[#4a3f35]" />
                      <p className="text-xs font-medium text-gray-600">Pallets</p>
                    </div>
                    <p className="text-xl font-bold text-[#4a3f35]">{selectedFarmer.current.pallets}</p>
                    {timeRange !== 'all' && (
                      <TrendBadge current={selectedFarmer.current.pallets} previous={selectedFarmer.previous.pallets} />
                    )}
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      <p className="text-xs font-medium text-gray-600">Invoiced</p>
                    </div>
                    <p className="text-xl font-bold text-[#4a3f35]">{selectedFarmer.invoicedPercentage.toFixed(1)}%</p>
                    {timeRange !== 'all' && (
                      <TrendBadge
                        current={selectedFarmer.current.invoiced}
                        previous={selectedFarmer.previous.invoiced}
                      />
                    )}
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-[#8cc63f]" />
                      <p className="text-xs font-medium text-gray-600">Avg Crates/Entry</p>
                    </div>
                    <p className="text-xl font-bold text-[#4a3f35]">
                      {selectedFarmer.current.entries > 0 ? (selectedFarmer.current.crates / selectedFarmer.current.entries).toFixed(1) : '0'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-[#4a3f35]" />
                      <p className="text-xs font-medium text-gray-600">Avg Pallets/Entry</p>
                    </div>
                    <p className="text-xl font-bold text-[#4a3f35]">
                      {selectedFarmer.current.entries > 0 ? (selectedFarmer.current.pallets / selectedFarmer.current.entries).toFixed(1) : '0'}
                    </p>
                  </div>
                </div>

                {/* Invoice Progress */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Invoice Progress</h3>
                    <span className="text-sm font-bold text-[#4a3f35]">{selectedFarmer.invoicedPercentage.toFixed(1)}%</span>
                  </div>
                  <InvoiceProgressBar percentage={selectedFarmer.invoicedPercentage} />
                </div>

                {/* Line Chart */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Crates and Pallets</h3>
                  {farmerLineData && farmerLineData.labels.length > 0 ? (
                    <div className="h-64 sm:h-80">
                      <Line data={farmerLineData} options={lineChartOptions} />
                    </div>
                  ) : (
                    <EmptyChart />
                  )}
                </div>

                {/* Invoice Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Invoice Status</h3>
                    {farmerInvoiceBarData && farmerInvoiceBarData.labels.length > 0 ? (
                      <div className="h-64 sm:h-80">
                        <Bar data={farmerInvoiceBarData} options={commonChartOptions} />
                      </div>
                    ) : (
                      <EmptyChart />
                    )}
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Invoice Summary</h3>
                    {farmerInvoiceDoughnutData && (selectedFarmer.current.invoiced > 0 || selectedFarmer.current.notInvoiced > 0) ? (
                      <div className="h-64 sm:h-80">
                        <Doughnut data={farmerInvoiceDoughnutData} options={doughnutOptions} />
                      </div>
                    ) : (
                      <EmptyChart message="No invoice data" />
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* Global Dashboard */
              <>
                {/* Summary Cards */}
                {loading ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <SkeletonCard key={i} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Entries</p>
                          <p className="mt-1 text-2xl sm:text-3xl font-bold text-[#4a3f35]">{globalStats?.current.entries || 0}</p>
                          {timeRange !== 'all' && globalStats && (
                            <TrendBadge current={globalStats.current.entries} previous={globalStats.previous.entries} />
                          )}
                        </div>
                        <div className="p-3 rounded-full bg-[#4a3f35]/10">
                          <FileText className="h-6 w-6 text-[#4a3f35]" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Crates</p>
                          <p className="mt-1 text-2xl sm:text-3xl font-bold text-[#4a3f35]">{globalStats?.current.crates || 0}</p>
                          {timeRange !== 'all' && globalStats && (
                            <TrendBadge current={globalStats.current.crates} previous={globalStats.previous.crates} />
                          )}
                        </div>
                        <div className="p-3 rounded-full bg-[#8cc63f]/10">
                          <Package className="h-6 w-6 text-[#8cc63f]" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Pallets</p>
                          <p className="mt-1 text-2xl sm:text-3xl font-bold text-[#4a3f35]">{globalStats?.current.pallets || 0}</p>
                          {timeRange !== 'all' && globalStats && (
                            <TrendBadge current={globalStats.current.pallets} previous={globalStats.previous.pallets} />
                          )}
                        </div>
                        <div className="p-3 rounded-full bg-[#4a3f35]/10">
                          <Package className="h-6 w-6 text-[#4a3f35]" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Invoiced %</p>
                          <p className="mt-1 text-2xl sm:text-3xl font-bold text-[#4a3f35]">{globalInvoicedPct.toFixed(1)}%</p>
                          {timeRange !== 'all' && globalStats && (
                            <TrendBadge current={globalStats.current.invoiced} previous={globalStats.previous.invoiced} />
                          )}
                        </div>
                        <div className="p-3 rounded-full bg-green-50">
                          <TrendingUp className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Charts Row 1 */}
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SkeletonChart />
                    <SkeletonChart />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Overall Invoice Status</h3>
                      {globalInvoiceDoughnutData && (globalStats?.current.invoiced || 0) + (globalStats?.current.notInvoiced || 0) > 0 ? (
                        <div className="h-64 sm:h-72">
                          <Doughnut data={globalInvoiceDoughnutData} options={doughnutOptions} />
                        </div>
                      ) : (
                        <EmptyChart message="No invoice data" />
                      )}
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Top Farmers</h3>
                      {topFarmersChartData && topFarmersChartData.labels.length > 0 ? (
                        <div className="h-64 sm:h-72">
                          <Bar data={topFarmersChartData} options={horizontalBarOptions} />
                        </div>
                      ) : (
                        <EmptyChart message="No farmer data" />
                      )}
                    </div>
                  </div>
                )}

                {/* Charts Row 2 */}
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SkeletonChart />
                    <SkeletonChart />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Top Customers</h3>
                      {topCustomersChartData && topCustomersChartData.labels.length > 0 ? (
                        <div className="h-64 sm:h-72">
                          <Bar data={topCustomersChartData} options={horizontalBarOptions} />
                        </div>
                      ) : (
                        <EmptyChart message="No customer data" />
                      )}
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-[#4a3f35] mb-4">Product Breakdown</h3>
                      {productBreakdownData && productBreakdownData.labels.length > 0 ? (
                        <div className="h-64 sm:h-72">
                          <Doughnut data={productBreakdownData} options={doughnutOptions} />
                        </div>
                      ) : (
                        <EmptyChart message="No product data" />
                      )}
                    </div>
                  </div>
                )}

                {/* Needs Attention */}
                {loading ? (
                  <SkeletonCard height="h-48" />
                ) : (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <h3 className="text-lg font-medium text-[#4a3f35]">Needs Attention</h3>
                    </div>
                    {globalStats?.needsAttention.length === 0 ? (
                      <p className="text-sm text-gray-500">All farmers are up to date with invoicing.</p>
                    ) : (
                      <div className="space-y-3">
                        {globalStats?.needsAttention.map((farmer, index) => (
                          <div
                            key={farmer.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => {
                              const f = farmers.find(fa => fa.id === farmer.id);
                              if (f) setSelectedFarmer(f);
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold text-gray-400 w-5">{index + 1}</span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{farmer.name}</p>
                                <p className="text-xs text-gray-500">{farmer.notInvoicedCount} not invoiced</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-red-600">{farmer.invoicedPercentage.toFixed(1)}%</p>
                              <p className="text-xs text-gray-500">invoiced</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
